import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppCard from "@toothfairy/shared-ui/AppCard";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTab from "@toothfairy/shared-ui/AppTab";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Popable } from "react-native-popable";
import { default as AppConfig } from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory } from "../../../../Router";
import Utility from "../../../../Utility";
import WorkspaceChip from "../../../Workspaces/Components/WorkspaceChip";
import QaPreselectionToolbar from "./SubComponents/QaPreselectionToolbar";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import { isMobileScreen } from "../../../MainViewContainer";
import QaHiringTemplate from "./SubComponents/QaHiringTemplate";
import SpawnAgentModal from "./SubComponents/SpawnAgentModal";
import agentUtilsAPI from "../../../../API/AgentUtils";
const extraDay = 24 * 60 * 60 * 1000;

const AgentGridView = ({
  agents,
  currentTheme,
  getActiveWorkspace,
  AppToasty,
  t,
  onCardPress,
  selectedAgentId,
}) => {
  return (
    <View
      style={{
        flexWrap: "wrap",
        justifyContent: "flex-start",
        flexDirection: "row",
      }}
      contentContainerStyle={{
        padding: 10,
      }}
    >
      {agents?.map((item) => (
        <div className="select_agent" key={item.id}>
          <AppCard
            rightIconPopupContent={item?.description}
            leftIconName={AppAssets.icons.taskAgent}
            isHoverable
            accessibilityLabel={`agent-card-${item.id}`}
            key={item.id}
            headerContainerStyle={{
              alignItems: "flex-start",
            }}
            style={{
              marginHorizontal: 20,
              borderColor:
                selectedAgentId === item?.id
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.app_outline_color,
              zIndex: -1,
            }}
            onCardPress={() => onCardPress(item)}
            cardWidth={300}
            cardHeight={260}
            heading={item?.label}
            shadowColor={currentTheme?.theme?.transparent}
            footerHeadingStyle={{
              fontSize: currentTheme.theme.small_font_size,
              fontWeight: "normal",
              paddingBottom: 10,
              maxWidth: 170,
            }}
            headingStyle={{
              maxWidth: 240,
              fontSize: 18,
              marginBottom: 5,
              marginTop: -3,
            }}
            subFooterHeadingStyle={{
              maxWidth: 170,
            }}
            rightIconSize={14}
            rightIconName={AppAssets?.icons?.info}
            rightIconDisplayed
            isRightIconClickable={
              item?.disabled !== true &&
              item?.activeUntil &&
              new Date(item.activeUntil).getTime() + extraDay > Date.now()
            }
            childrenViewStyle={{
              justifyContent: "flex-end",
              zIndex: -1,
            }}
            subHeading={item?.department
              ?.toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())}
            subHeadingStyle={{
              fontSize: currentTheme?.theme?.extra_small_font_size,
              marginLeft: 10,
              fontWeight: "bold",
            }}
            subHeadingColor={currentTheme?.theme?.orange}
            subHeadingIcon={
              item?.department
                ? Utility.mapDepartmentWithIcon(item?.department, AppAssets)
                : null
            }
            subHeadingIconSize={15}
            subHeadingIconColor={currentTheme?.theme?.orange}
          >
            <View
              style={{
                flexDirection: "column",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                height: 170,
                zIndex: -1,
              }}
            >
              {generateAgentChips(
                item,
                currentTheme,
                getActiveWorkspace()?.subscriptionType
              )?.map((chip, index) =>
                chip?.isPlusOnly ? (
                  <Popable
                    key={index}
                    numberOfLines={10}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 30,
                        zIndex: 20,
                        maxWidth: 200,
                        width: 200,
                      },
                    ]}
                    content={chip?.remainingSkillsString}
                    action="hover"
                    position="bottom"
                  >
                    <WorkspaceChip
                      label={chip.label}
                      leftIcon={chip.icon}
                      {...(chip.customStyle && {
                        backgroundColor: chip.customStyle.backgroundColor,
                        fontColor: chip.customStyle.fontColor,
                      })}
                    />
                  </Popable>
                ) : (
                  <WorkspaceChip
                    key={index}
                    label={chip.label}
                    leftIcon={chip.icon}
                    {...(chip.customStyle && {
                      backgroundColor: chip.customStyle.backgroundColor,
                      fontColor: chip.customStyle.fontColor,
                    })}
                  />
                )
              )}
            </View>
          </AppCard>
        </div>
      ))}
    </View>
  );
};

const formatDepartmentLabel = (department) =>
  department
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AgentPreviewPanel = ({
  agent,
  currentTheme,
  t,
  getActiveWorkspace,
  onHire,
}) => {
  const theme = currentTheme?.theme;
  const subscriptionType = getActiveWorkspace()?.subscriptionType;
  const chips = agent
    ? generateAgentChips(agent, currentTheme, subscriptionType, 0)
    : [];

  const hiringTemplate =
    typeof agent?.agentHiringTemplate === "string"
      ? JSON.parse(agent?.agentHiringTemplate)
      : agent?.agentHiringTemplate;
  const needsApiKeys =
    !!hiringTemplate?.fields?.some(
      (f) => f?.requiredSecrets && f.requiredSecrets.length > 0,
    );
  const resourceCount =
    (hiringTemplate?.resources?.dataSources?.length || 0) +
    (hiringTemplate?.resources?.executionEnvironments?.length || 0) +
    (hiringTemplate?.resources?.functions?.length || 0) +
    (hiringTemplate?.resources?.prompts?.length || 0) +
    (hiringTemplate?.resources?.agents?.length || 0);

  return (
    <View
      style={{
        width: 360,
        borderLeftWidth: 1,
        borderLeftColor: theme?.lightGrey,
        flexDirection: "column",
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {!agent ? (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: 30,
          }}
        >
          <AppPlaceholderView
            wrapperStyle={{
              width: "80%",
              height: "100%",
              marginVertical: "auto",
              marginHorizontal: "auto",
              alignItems: "center",
            }}
            mainCaption={t("settings.qa.noAgentSelected", "No agent selected")}
            subCaption={t(
              "settings.qa.noAgentSelectedDesc",
              "Select an agent from the list to preview its details and hiring options",
            )}
            size={30}
            icon={AppAssets.icons.taskAgent}
          />
        </View>
      ) : (
        <>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 24, paddingBottom: 12 }}
            showsVerticalScrollIndicator
          >
            <AppText
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: theme?.mediumGrey,
              }}
            >
              {t("settings.qa.preview")}
            </AppText>
            <AppText
              style={{ fontSize: 20, fontWeight: "600", marginTop: 10 }}
              preventTranslation
            >
              {agent?.label}
            </AppText>
            {!!agent?.department && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <AppIcon
                  icon={Utility.mapDepartmentWithIcon(
                    agent?.department,
                    AppAssets
                  )}
                  size={13}
                  color={theme?.orange}
                />
                <AppText
                  style={{
                    fontSize: 11,
                    fontWeight: "bold",
                    color: theme?.orange,
                    marginLeft: 6,
                  }}
                  preventTranslation
                >
                  {formatDepartmentLabel(agent?.department)}
                </AppText>
              </View>
            )}
            {!!agent?.description && (
              <AppText
                style={{
                  fontSize: 13,
                  lineHeight: 20,
                  color: theme?.darkGrey,
                  marginTop: 16,
                }}
                preventTranslation
              >
                {agent?.description}
              </AppText>
            )}
            {chips?.length > 0 && (
              <>
                <AppText
                  style={{
                    fontSize: 11,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: theme?.mediumGrey,
                    marginTop: 24,
                    marginBottom: 10,
                  }}
                >
                  {t("settings.qa.capabilitiesLabel")}
                </AppText>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {chips.map((chip, index) => (
                    <WorkspaceChip
                      key={index}
                      label={chip.label}
                      leftIcon={chip.icon}
                      {...(chip.customStyle && {
                        backgroundColor: chip.customStyle.backgroundColor,
                        fontColor: chip.customStyle.fontColor,
                      })}
                    />
                  ))}
                </View>
              </>
            )}
            {!!agent?.agentHiringTemplate && (
              <View
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTopWidth: 1,
                  borderTopColor: theme?.lightGrey,
                }}
              >
                <AppText
                  style={{
                    fontSize: 11,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: theme?.mediumGrey,
                    marginBottom: 10,
                  }}
                >
                  {t("settings.qa.setupRequirements")}
                </AppText>
                {needsApiKeys && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <AppIcon
                      icon={AppAssets.icons.info}
                      size={12}
                      color={theme?.orange}
                    />
                    <AppText
                      style={{
                        fontSize: 12,
                        color: theme?.orange,
                        marginLeft: 6,
                      }}
                    >
                      {t("settings.qa.apiKeysRequired")}
                    </AppText>
                  </View>
                )}
                {resourceCount > 0 && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <AppIcon
                      icon={AppAssets.icons.info}
                      size={12}
                      color={theme?.primary}
                    />
                    <AppText
                      style={{
                        fontSize: 12,
                        color: theme?.primary,
                        marginLeft: 6,
                      }}
                    >
                      {t("settings.qa.resourcesToImport", {
                        count: resourceCount,
                      })}
                    </AppText>
                  </View>
                )}
              </View>
            )}
          </ScrollView>
          <View
            style={{
              padding: 20,
              borderTopWidth: 1,
              borderTopColor: theme?.lightGrey,
            }}
          >
            <ToothFairyButton
              title={t("settings.qa.hireThisAgent")}
              onPress={() => onHire(agent)}
              wrapperStyle={{ width: "100%" }}
              style={{ marginHorizontal: 0, height: 38 }}
            />
            {!!agent?.agentHiringTemplate && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                <AppIcon
                  icon={AppAssets.icons.wizard}
                  size={12}
                  color={theme?.mediumGrey}
                />
                <AppText
                  style={{
                    fontSize: 11,
                    color: theme?.mediumGrey,
                    marginLeft: 6,
                    textAlign: "center",
                  }}
                >
                  {t("settings.qa.includesGuidedSetup")}
                </AppText>
              </View>
            )}
          </View>
        </>
      )}
    </View>
  );
};

export const adaptModeCodeToLabel = (mode) => {
  switch (mode) {
    case "speed":
      return "Operator";
    case "accuracy":
      return "Operator";
    case "coder":
      return "Programmer";
    case "mathematician":
      return "Mathematician";
    case "chatter":
      return "Assistant";
    case "planner":
      return "Orchestrator";
    case "computer":
      return "Desktop agent";
    case "retriever":
      return "Operator";
    case "voice":
      return "Voice";
    default:
      return "Unknown";
  }
};

// Function to generate all possible chips based on item
const generateAgentChips = (
  item,
  currentTheme,
  subscriptionType,
  maxVisible = 6
) => {
  let remainingSkillsString = null;
  const MAX_VISIBLE_CHIPS = maxVisible;

  // Function to generate all possible chips based on item
  const allChips = [
    {
      condition: item?.mode != null,
      label: adaptModeCodeToLabel(item?.mode),
      icon: Utility.mapAgentTypeWithIcon(item, AppAssets),
      customStyle: {
        backgroundColor: currentTheme?.theme?.primary,
        fontColor: currentTheme?.theme?.white,
      },
    },
    {
      condition: item?.agentHiringTemplate != null,
      label: "Hiring wizard",
      icon: AppAssets?.icons?.wizard,
      customStyle: {
        backgroundColor: currentTheme?.theme?.orange,
        fontColor: currentTheme?.theme?.white,
      },
    },
    {
      condition: item?.agenticRAG,
      label: "Agentic Tooling",
      icon: AppAssets.icons.agentic,
      customStyle: {
        backgroundColor: currentTheme?.theme?.purple,
        fontColor: currentTheme?.theme?.white,
      },
    },
    {
      condition: item?.hasCode,
      label: "Code execution",
      icon: AppAssets.icons.code,
    },
    {
      condition: item?.allowImagesGeneration,
      label: "Image creation",
      icon: AppAssets.icons.art,
    },
    {
      condition: item?.agenticRAG && item?.agentDesktopVM,
      label: "Browser",
      icon: AppAssets.icons.computerUser,
    },
    {
      condtion: item?.allowVideosGeneration,
      label: "Video generation",
      icon: AppAssets.icons.video,
    },
    {
      condition: item?.allow3dModelGeneration,
      label: "3D model generation",
      icon: AppAssets.icons.model3d,
    },
    {
      condition: item?.allowDocsUpload,
      label: "Files upload",
      icon: AppAssets.icons.files,
    },
    {
      condition: item?.allowImagesUpload,
      label: "Image analysis",
      icon: AppAssets.icons.image,
    },
    {
      condition: item?.charting,
      label: "Graphs enabled",
      icon: AppAssets.icons.chartingAgent,
    },
    {
      condition: item?.allowAudiosGeneration,
      label: "Audio generation",
      icon: AppAssets.icons.audio,
    },
    {
      condition: item?.blender,
      label: "Reasoning",
      icon: AppAssets.icons.advancedReasoning,
    },
    {
      condition: item?.hasNER,
      label: "Entity recognition",
      icon: AppAssets.icons.ner,
    },
    {
      condition: item?.advancedImageGeneration,
      label: "High-fidelity",
      icon: AppAssets.icons.advancedImage,
    },
    {
      condition: item?.allowVideosUpload,
      label: "Video analysis",
      icon: AppAssets.icons.video,
    },
    {
      condition: item?.allowAudiosUpload,
      label: "Audio Analysis",
      icon: AppAssets.icons.audio,
    },
    {
      condition: item?.promptBuffering,
      label: "Prompt buffering",
      icon: AppAssets.icons.promptBuffering,
    },
    {
      condition: item?.allowDeepInternetSearch,
      label: "Deep research",
      icon: AppAssets.icons.deepSearch,
    },
    {
      condition: item?.hasModeration,
      label: "Content safety",
      icon: AppAssets.icons.shield,
    },
    {
      condition: item?.chainOfThoughts,
      label: "Tree of thoughts",
      icon: AppAssets.icons.treeOfThoughts,
    },
    {
      condition: item?.hasContentCreation,
      label: "Content creation",
      icon: AppAssets.icons.writer,
    },
    {
      condition: item?.hasEntityExtraction,
      label: "Entities extraction",
      icon: AppAssets.icons.entities,
    },
    {
      condition: item?.topK > 10,
      label: "Bulk retrieval",
      icon: AppAssets?.icons?.collection,
    },
  ];
  // Filter chips that should be displayed based on their conditions
  const visibleChips = allChips.filter((chip) => chip.condition);

  // If we have more than MAX_VISIBLE_CHIPS, slice and add the "more" chip
  if (MAX_VISIBLE_CHIPS > 0 && visibleChips.length > MAX_VISIBLE_CHIPS) {
    const displayedChips = visibleChips.slice(0, MAX_VISIBLE_CHIPS);
    const remainingCount = visibleChips.length - MAX_VISIBLE_CHIPS;
    const remainingChips = visibleChips.slice(MAX_VISIBLE_CHIPS);
    remainingSkillsString = remainingChips.map((chip) => chip.label).join(", ");

    displayedChips.push({
      label: `${remainingCount} more skills`,
      icon: AppAssets.icons.more,
      isPlusOnly: true,
      remainingSkillsString,
    });

    return displayedChips;
  } else {
    return visibleChips;
  }
};

const QaPreSelection = ({ handleCreation, setShowPreselectionModal }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const history = useHistory();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const isLargerScreen = width > 768;
  const [searchTerm, setSearchTerm] = React.useState("");
  const [agentType, setAgentType] = React.useState(null);
  const [agentDepartment, setAgentDepartment] = React.useState(null);
  const [hireableAgents, setHireableAgents] = React.useState([]);
  const [filteredAgents, setFilteredAgents] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(null);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [agentHiringTemplate, setAgentHiringTemplate] = useState(null);
  const [isHiringTemplateModalOpen, setIsHiringTemplateModalOpen] =
    React.useState(false);
  const [isSpawnAgentModalOpen, setIsSpawnAgentModalOpen] = React.useState(
    false,
  );
  const [selectedAgentId, setSelectedAgentId] = React.useState(null);
  const showPreview = width >= 1100;
  const searchTimeoutRef = React.useRef(null);
  const isFirstRenderRef = React.useRef(true);

  const auGovAgents = useMemo(() => {
    return hireableAgents.filter((agent) => {
      try {
        const template =
          typeof agent.agentHiringTemplate === "string"
            ? JSON.parse(agent.agentHiringTemplate)
            : agent.agentHiringTemplate;
        return template?.AuGov === "true";
      } catch {
        return false;
      }
    });
  }, [hireableAgents]);

  const isAuGovWorkspace = useMemo(() => {
    const rootUserId = getActiveWorkspace()?.userID;
    const rootUser = state?.workspaceUsers?.find((u) => u?.id === rootUserId);
    return rootUser?.user_email?.toLowerCase()?.endsWith(".gov.au") || false;
  }, [state?.workspaceUsers, getActiveWorkspace()?.userID]);

  const openHiringModal = (item) => {
    setIsHiringTemplateModalOpen(true);
    setAgentHiringTemplate(item);
  };

  const handleAgentAction = (item) => {
    if (item?.agentHiringTemplate) {
      openHiringModal({
        ...item,
        agentHiringTemplate:
          typeof item?.agentHiringTemplate == "string"
            ? JSON.parse(item?.agentHiringTemplate)
            : item?.agentHiringTemplate,
      });
    } else {
      handleCreation(item?.id, item?.label);
    }
  };

  const handleCardPress = (item) => {
    if (showPreview) {
      setSelectedAgentId(item?.id);
    } else {
      handleAgentAction(item);
    }
  };

  const selectedAgent = useMemo(
    () =>
      filteredAgents?.find((a) => a?.id === selectedAgentId) ||
      auGovAgents?.find((a) => a?.id === selectedAgentId) ||
      null,
    [filteredAgents, auGovAgents, selectedAgentId]
  );

  // No agent is pre-selected by default — the user must click one.
  // Keep the preview selection valid as the list loads / filters change
  useEffect(() => {
    if (!filteredAgents?.length) return;
    if (
      selectedAgentId &&
      !filteredAgents.some((a) => a?.id === selectedAgentId)
    ) {
      setSelectedAgentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAgents]);

  const handleAIGeneratedAgent = (agentConfig) => {
    // Call handleCreation with "new" as ID and the AI-generated config
    handleCreation("new", agentConfig.label, agentConfig);
  };

  // Fetch hireable agents from API
  useEffect(() => {
    const fetchHireableAgents = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);
        const response = await agentUtilsAPI.fetchHireableAgents();
        // The response contains an "items" array with the hireable agents
        const rawAgents =
          response?.items || response?.data?.items || response || [];

        // Filter out tool/prompt container agents (not shown in agent hiring)
        const agents = rawAgents.filter((agent) => {
          try {
            const template =
              typeof agent.agentHiringTemplate === "string"
                ? JSON.parse(agent.agentHiringTemplate)
                : agent.agentHiringTemplate;
            return !template?.isToolsPromptsContainer;
          } catch {
            return true;
          }
        });

        setHireableAgents(agents);
        setFilteredAgents(agents);
      } catch (error) {
        console.error("Error fetching hireable agents:", error);
        setFetchError(error.message || "Failed to fetch hireable agents");
        AppToasty.show(t("settings.qa.failedToLoadHireableAgents"), {
          type: "danger",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchHireableAgents();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [hireableAgents?.length]);

  const handleSearch = () => {
    try {
      setFilteredAgents(
        hireableAgents
          ?.filter((agent) => {
            const term = searchTerm.toLowerCase();
            return (
              (agent.label.toLowerCase().includes(term) ||
                agent.description.toLowerCase().includes(term)) &&
              (agent.mode?.toLowerCase() == agentType ||
                agentType == null ||
                agentType == "[object Object]") &&
              (agent.department == agentDepartment ||
                agentDepartment == null ||
                agentDepartment == "[object Object]")
            );
          })
          ?.sort((a, b) => {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          })
      );
    } catch (error) {
      console.log(error);
    }
  };

  // Debounced search effect - prevents focus loss by delaying search
  useEffect(() => {
    // Skip on first render - initial search is handled by hireableAgents effect
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout - wait 300ms after last keystroke before searching
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, agentType, agentDepartment]);
  const hiringTemplateModal = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: "auto",
          minHeight: 250,
          maxWidth: 600,
          maxHeight: "95%",
          width: isMobileScreen() ? "99%" : "90%",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={"hiring_selection"}
        isModalOpen={isHiringTemplateModalOpen}
        setIsModalOpen={setIsHiringTemplateModalOpen}
        modalTitle={t("settings.qa.agentHiringWizard")}
        closeModal={() => {
          setIsHiringTemplateModalOpen(false);
          setAgentHiringTemplate(null);
        }}
      >
        <QaHiringTemplate
          item={agentHiringTemplate}
          setIsHiringTemplateModalOpen={setIsHiringTemplateModalOpen}
          setShowPreselectionModal={setShowPreselectionModal}
          onAgentCreated={(agentId) => {
            setIsHiringTemplateModalOpen(false);
            setShowPreselectionModal(false);
            history.push(
              `/workspaces/${getActiveWorkspace()?.id}/agents/${agentId}`,
            );
          }}
        />
      </ToothFairyModal>
    );
  }, [
    isHiringTemplateModalOpen,
    JSON.stringify(agentHiringTemplate),
    setShowPreselectionModal,
  ]);
  return (
    <View
      style={{
        zIndex: -1,
        flex: 1,
        marginRight: -5,
        marginLeft: -10,
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder caption="messages.loadingHireableAgents" />
      ) : (
        <AppToast.ToastProvider
          {...AppConfig.notificationConfig}
          // offset for both top and bottom toasts
        >
          <View
            style={{
              flexDirection: "row",
              flex: 1,
              minHeight: 0,
            }}
          >
            <View
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                flexDirection: "column",
              }}
            >
              <View
                style={{
                  padding: 24,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: currentTheme?.theme?.lightGrey,
                }}
              >
                <AppText
                  style={{
                    fontSize: 22,
                    fontWeight: "600",
                    color: currentTheme?.theme?.font_color,
                  }}
                >
                  {t("settings.list.newAgent")}
                </AppText>
                <AppText
                  style={{
                    fontSize: 13,
                    color: currentTheme?.theme?.darkGrey,
                    marginTop: 4,
                    lineHeight: 19,
                  }}
                >
                  Create a custom agent or choose from pre-built templates
                </AppText>
                <View
                  style={{
                    flexDirection: "row",
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  <ToothFairyButton
                    title="Create Agent"
                    onPress={() => handleCreation("new", "New agent")}
                    style={{ marginHorizontal: 0, height: 34 }}
                    wrapperStyle={{ minWidth: 120 }}
                  />
                  <ToothFairyButton
                    title="Create with AI"
                    isSecondary
                    fontColor={currentTheme?.theme?.font_color}
                    onPress={() => setIsSpawnAgentModalOpen(true)}
                    style={{ marginHorizontal: 0, height: 34 }}
                    wrapperStyle={{ minWidth: 120 }}
                  />
                </View>
              </View>
              <View
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <QaPreselectionToolbar
                  handleSearch={handleSearch}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  agentType={agentType}
                  setAgentType={setAgentType}
                  agentDepartment={agentDepartment}
                  setAgentDepartment={setAgentDepartment}
                />
              {isAuGovWorkspace ? (
                <View style={{ marginHorizontal: 15 }}>
                  <AppTab
                    routes={[
                      { key: "global", title: "Global" },
                      { key: "auGov", title: "AU Gov (Preview)" },
                    ]}
                    renderSceneObject={{
                      global: () => (
                        <AgentGridView
                          agents={filteredAgents}
                          currentTheme={currentTheme}
                          getActiveWorkspace={getActiveWorkspace}
                          AppToasty={AppToasty}
                          t={t}
                          onCardPress={handleCardPress}
                          selectedAgentId={showPreview ? selectedAgentId : null}
                        />
                      ),
                      auGov: () => (
                        <AgentGridView
                          agents={auGovAgents}
                          currentTheme={currentTheme}
                          getActiveWorkspace={getActiveWorkspace}
                          AppToasty={AppToasty}
                          t={t}
                          onCardPress={handleCardPress}
                          selectedAgentId={showPreview ? selectedAgentId : null}
                        />
                      ),
                    }}
                    isAnimated={false}
                    tabMinWidth={130}
                    appTabWrapperStyle={{
                      marginLeft: 10,
                    }}
                  />
                </View>
              ) : (
                <AgentGridView
                  agents={filteredAgents}
                  currentTheme={currentTheme}
                  getActiveWorkspace={getActiveWorkspace}
                  AppToasty={AppToasty}
                  t={t}
                  onCardPress={handleCardPress}
                  selectedAgentId={showPreview ? selectedAgentId : null}
                />
              )}
              </View>
            </View>
            {showPreview && (
              <AgentPreviewPanel
                agent={selectedAgent}
                currentTheme={currentTheme}
                t={t}
                getActiveWorkspace={getActiveWorkspace}
                onHire={handleAgentAction}
              />
            )}
          </View>
        </AppToast.ToastProvider>
      )}
      {hiringTemplateModal}

      {/* Spawn Agent Modal */}
      <ToothFairyModal
        modalStyle={{
          height: "auto",
          minHeight: 400,
          maxWidth: 600,
          width: isMobileScreen() ? "99%" : "90%",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={"spawn_agent"}
        isModalOpen={isSpawnAgentModalOpen}
        setIsModalOpen={setIsSpawnAgentModalOpen}
        modalTitle={t("settings.qa.agentCreator")}
        closeModal={() => {
          setIsSpawnAgentModalOpen(false);
        }}
      >
        <SpawnAgentModal
          setIsSpawnAgentModalOpen={setIsSpawnAgentModalOpen}
          setShowPreselectionModal={setShowPreselectionModal}
          onAgentGenerated={handleAIGeneratedAgent}
        />
      </ToothFairyModal>
    </View>
  );
};

export default QaPreSelection;
